import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Icon } from '../components/icons';
import { LanguagePicker } from '../components/LanguagePicker';
import { DeleteButton, Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { useT } from '../i18n';
import type { Interpreter } from '../types';

export function InterpretersPage() {
  const t = useT();
  const [editing, setEditing] = useState<Interpreter | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const list = useQuery({ queryKey: ['interpreters'], queryFn: () => api.get<Interpreter[]>('/interpreters') });
  const q = search.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (i) => (showInactive || i.active) && (!q || `${i.name} ${i.languages.join(' ')} ${i.email ?? ''}`.toLowerCase().includes(q)),
  );
  return (
    <>
      <PageHead
        title={t('interp.title')}
        sub={t('interp.sub')}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon.plus />
            {t('interp.new')}
          </button>
        }
      />
      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('interp.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
        <label className="checkbox">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('crew.showInactive')}
        </label>
      </div>
      <div className="card table-wrap">
        {list.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title={t('interp.emptyTitle')} text={t('interp.emptyText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('crew.name')}</th>
                <th>{t('interp.languages')}</th>
                <th>{t('clients.phone')}</th>
                <th>{t('login.email')}</th>
                <th className="right">{t('interp.events')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="clickable" onClick={() => setEditing(i)} style={{ opacity: i.active ? 1 : 0.55 }}>
                  <td>
                    <strong>{i.name}</strong>
                    {!i.active && <span className="badge" style={{ marginLeft: 6 }}>{t('crew.inactive')}</span>}
                  </td>
                  <td>{i.languages.map((l) => <span key={l} className="badge" style={{ marginRight: 4 }}>{l}</span>)}</td>
                  <td>{i.phone && <a href={`tel:${i.phone}`} onClick={(e) => e.stopPropagation()}>{i.phone}</a>}</td>
                  <td className="muted">{i.email}</td>
                  <td className="right num">{i.eventCount}</td>
                  <td className="right"><button className="btn btn-sm">{t('common.edit')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <InterpreterModal interpreter={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function InterpreterModal({ interpreter, onClose }: { interpreter?: Interpreter; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: interpreter?.name ?? '',
    phone: interpreter?.phone ?? '',
    email: interpreter?.email ?? '',
    notes: interpreter?.notes ?? '',
    active: interpreter?.active ?? true,
  });
  const [languages, setLanguages] = useState<string[]>(interpreter?.languages ?? []);
  const set = (k: 'name' | 'phone' | 'email' | 'notes') => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const done = () => {
    qc.invalidateQueries({ queryKey: ['interpreters'] });
    qc.invalidateQueries({ queryKey: ['event'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, phone: f.phone || null, email: f.email || null, notes: f.notes || null, active: f.active, languages };
      return interpreter ? api.patch(`/interpreters/${interpreter.id}`, body) : api.post('/interpreters', body);
    },
    onSuccess: done,
  });
  return (
    <Modal
      wide
      title={interpreter ? interpreter.name : t('interp.new')}
      onClose={onClose}
      footer={
        <>
          {interpreter && (
            <DeleteButton
              name={interpreter.name}
              path={`/interpreters/${interpreter.id}`}
              blockedCode="interpreter_has_events"
              alternative={
                interpreter.active
                  ? { label: t('interp.deactivateInstead'), run: () => api.patch(`/interpreters/${interpreter.id}`, { active: false }) }
                  : undefined
              }
              onDone={done}
            />
          )}
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('crew.name')} full htmlFor="in-name">
          <input id="in-name" className="input" value={f.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={t('clients.phone')} htmlFor="in-phone">
          <input id="in-phone" className="input" type="tel" value={f.phone} onChange={set('phone')} />
        </Field>
        <Field label={t('login.email')} htmlFor="in-email">
          <input id="in-email" className="input" type="email" value={f.email} onChange={set('email')} />
        </Field>
        <Field label={t('interp.languages')} hint={t('interp.languagesHint')} full>
          <LanguagePicker value={languages} onChange={setLanguages} />
        </Field>
        <Field label={t('common.notes')} full htmlFor="in-notes">
          <textarea id="in-notes" className="textarea" value={f.notes} onChange={set('notes')} />
        </Field>
        {interpreter && (
          <div className="full">
            <label className="checkbox">
              <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
              {t('interp.activeLabel')}
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
